import { sortByOrderNumber } from "./GatewaysSummary";
import {
  Gateway,
  Task,
  TaskResult,
  TaskType,
} from "pages/ProjectManagement/ProjectManagement.types";
import { parseListDate } from "helpers/parseListDate";
import { customField } from "components/helpers/CustomFieldRenderers/CustomFormFields/model";

import React from "react";

const GatewayPdf: React.ForwardRefRenderFunction<HTMLDivElement, { gateway: Gateway | null }> = (
  { gateway },
  ref
) => {
  return (
    <div ref={ref} className="GatewaySummaryPDF">
      <style type="text/css" media="print">
        {
          "\
        @page {  size: A4 ;margin: 25mm 25mm 25mm 25mm; }\
        "
        }
        {
          "@page {\
  @bottom-left {\
    content: counter(page) ' of ' counter(pages);\
  }\
}"
        }
      </style>
      {gateway && (
        <>
          <h2 className="Title">{gateway.gatewayName}</h2>
          {gateway.siteID && (
            <div className="Row">
              <p>
                <b>Principle ID: </b>
              </p>
              <p>{gateway.siteID}</p>
            </div>
          )}
          {gateway.projectID && (
            <div className="Row">
              <p>
                <b>Project ID: </b>
              </p>
              <p>{gateway.projectID}</p>
            </div>
          )}
          {gateway.clientID && (
            <div className="Row">
              <p>
                <b>Client ID: </b>
              </p>
              <p>{gateway.clientID}</p>
            </div>
          )}
          {gateway.siteName && gateway.siteAddress && (
            <div className="Gap">
              {gateway.siteName && (
                <div className="Row">
                  <p>
                    <b>Site: </b>
                  </p>
                  <p>{gateway.siteName}</p>
                </div>
              )}
              {gateway.siteAddress && (
                <div className="Row">
                  <p>
                    <b>Address: </b>
                  </p>
                  <p>{gateway.siteAddress}</p>
                </div>
              )}
            </div>
          )}
          {gateway.description && (
            <p>
              <b>Description - </b>
              {gateway.description}
            </p>
          )}

          {gateway.tasks?.sort(sortByOrderNumber).map((task, index) => {
            return (
              <div className="TaskTable" key={index}>
                {task.taskType === TaskType.MILESTONE && milestonetask(task)}
                {task.taskType === TaskType.STANDARD_TASK && standardTask(task)}
                {task.taskType === TaskType.CUSTOM_TASK && customTask(task)}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default React.forwardRef(GatewayPdf);

const standardTask = (task: Task) => {
  return (
    <>
      <h4 className="GatewayTitle">{task.taskName}</h4>
      <div className="Row">
        <div className="Label">Outcome</div>
        <div className="Value">{taskResult(task.taskResult)}</div>
      </div>
    </>
  );
};

const customTask = (task: Task) => {
  return (
    <>
      <h4 className="GatewayTitle">{task.taskName}</h4>
      {task.customFields &&
        task.customFields.length > 0 &&
        task.customFields.map((customField, index) => customFieldRender(customField, index))}
    </>
  );
};

const milestonetask = (task: Task) => {
  return (
    <>
      <h4 className="GatewayTitle">{task.taskName}</h4>
      {task.bPlannedActive && (
        <div className="Row">
          <div className="Label">Planned</div>
          <div className="Value">
            {task.plannedDate ? parseListDate(task.plannedDate, false) : ""}
          </div>
        </div>
      )}
      {task.bForecastActive && (
        <div className="Row">
          <div className="Label">Forecast</div>
          <div className="Value">
            {task.forecastDate ? parseListDate(task.forecastDate, false) : ""}
          </div>
        </div>
      )}
      {task.bActualActive && (
        <div className="Row">
          <div className="Label">Actual</div>
          <div className="Value">
            {task.actualDate ? parseListDate(task.actualDate, false) : ""}
          </div>
        </div>
      )}
      {task.taskResult && task.taskType === TaskType.STANDARD_TASK && (
        <div className="Row">
          <div className="Label">Outcome</div>
          <div className="Value">{taskResult(task.taskResult)}</div>
        </div>
      )}
      {task.customFields &&
        task.customFields.length > 0 &&
        task.customFields.map((customField, index) => customFieldRender(customField, index))}
    </>
  );
};
const customFieldRender = (customField: customField, index: number) => {
  let displayValue: string | undefined = "";
  let value =
    customField.customFieldType !== "MultiSelect" && customField.customFieldType !== "SingleSelect"
      ? customField.value
      : customField.possibleValuesIds;

  switch (customField.customFieldType) {
    case "Boolean":
      displayValue = value === "True" ? "Yes" : "No";
      break;
    case "String":
    case "Integer":
    case "Float":
    case "Currency":
    case "TextArea":
      displayValue = value as string;
      break;
    case "Date":
    case "DateTime":
      displayValue = parseListDate(value as string, true);
      break;
    case "SingleSelect":
      if (customField.possibleValuesIds == null) {
        break;
      } else if (
        Array.isArray(customField.possibleValuesIds) &&
        customField.possibleValuesIds.length > 0
      ) {
        displayValue = customField.possibleValues.find(
          (o) => o.id === (value as number[])[0]
        )?.value;
      }
      break;
    case "MultiSelect":
      displayValue = (value as number[])
        ?.map((id) => customField.possibleValues.find((o) => o.id === id)?.value)
        .join(", ");
      break;
  }

  return customField.customFieldType === "TextArea" ? (
    <div className="Row">
      <div className="TextArea">
        <b> {customField.name}:</b> {displayValue ?? ""}
      </div>
    </div>
  ) : (
    <div className="Row">
      <div className="Label">{customField.name}</div>
      <div className="Value">{displayValue && displayValue.length > 0 ? displayValue : ""}</div>
    </div>
  );
};

const taskResult = (taskResult: TaskResult | null) => {
  switch (taskResult) {
    case TaskResult.YES:
      return "Yes";
    case TaskResult.NO:
      return "No";
    case TaskResult.N_A:
      return "Not applicable";
    default:
      return "";
  }
};
